/**
 * Persistência de ocorrências/faltas no modo demo (localStorage em JSON).
 * Não substitui ficheiros no repositório em runtime; serve para refresh e planos futuros
 * (exportar o JSON, sincronizar fixtures, etc.).
 */

import { DEMO_TURMA_DISC } from "./buildDemoOccurrences";

const STORAGE_KEY = "waybee-class-diary-demo-occurrences-v1";

/**
 * @param {string} occurrenceDate ex.: "2026-04-15 08:00:00"
 * @param {string|number} idTurmaDisc
 * @param {string|number} scheduleId
 */
export function demoOccurrenceSessionKey(occurrenceDate, idTurmaDisc, scheduleId) {
  const d = occurrenceDate == null ? "" : String(occurrenceDate).trim();
  return `${String(idTurmaDisc)}|${String(scheduleId)}|${d}`;
}

function readRoot() {
  if (typeof localStorage === "undefined") {
    return {};
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writeRoot(root) {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(root));
  } catch {
    // quota / privado
  }
}

/**
 * @returns {{ byRa: Record<string, { occurrences: object[], absence: boolean }> } | null}
 */
export function loadDemoOccurrenceSession(sessionKey) {
  const root = readRoot();
  const entry = root[sessionKey];
  if (!entry || typeof entry !== "object") {
    return null;
  }
  return entry;
}

/**
 * Aplica o payload de POST (occurrenceData) ao estado em memória + localStorage.
 * Merge incremental sobre o snapshot já guardado para a mesma sessão.
 * @param {object} body { occurrenceData?: object, externalData?: object }
 */
export function applyDemoOccurrenceSave(body) {
  const od = body && body.occurrenceData;
  if (!od || String(od.idTurmaDisc) !== String(DEMO_TURMA_DISC)) {
    return;
  }

  const sessionKey = demoOccurrenceSessionKey(
    od.date,
    od.idTurmaDisc,
    od.scheduleId
  );

  const existing = loadDemoOccurrenceSession(sessionKey);
  const byRa = {};

  if (existing && existing.byRa) {
    Object.keys(existing.byRa).forEach(ra => {
      const key = String(ra);
      byRa[key] = {
        occurrences: [...(existing.byRa[ra].occurrences || [])],
        absence: !!existing.byRa[ra].absence
      };
    });
  }

  const ensureStudent = ra => {
    const key = String(ra);
    if (!byRa[key]) {
      byRa[key] = { occurrences: [], absence: false };
    }
  };

  (od.removed || []).forEach(rm => {
    const ra = rm.studentId || rm.ra;
    if (ra == null) {
      return;
    }
    ensureStudent(ra);
    const key = String(ra);
    byRa[key].occurrences = (byRa[key].occurrences || []).filter(o => {
      if (rm.idOccur != null) {
        return String(o.idOccur) !== String(rm.idOccur);
      }
      return !(
        String(o.idType) === String(rm.idType) &&
        String(o.idGroup) === String(rm.idGroup) &&
        (o.description || "") === (rm.description || "")
      );
    });
  });

  (od.occurrences || []).forEach((occ, idx) => {
    const ra = occ.studentId;
    if (ra == null) {
      return;
    }
    ensureStudent(ra);
    const key = String(ra);
    const idOccur = occ.idOccur || `demo-${Date.now()}-${idx}`;
    const id = `${occ.idType}-${occ.idGroup}`;
    byRa[key].occurrences.push({
      id,
      idType: occ.idType,
      idGroup: occ.idGroup,
      idOccur,
      description: occ.description || "",
      attachmentsCount: occ.files && occ.files.length ? occ.files.length : 0
    });
  });

  (od.absences || []).forEach(ab => {
    const ra = ab.studentId;
    if (ra == null) {
      return;
    }
    ensureStudent(ra);
    const key = String(ra);
    byRa[key].absence = ab.value === "A";
  });

  const root = readRoot();
  root[sessionKey] = { byRa };
  writeRoot(root);
}

/**
 * Junta dados persistidos à lista base de alunos (GET ocorrências).
 */
export function mergePersistedDemoOccurrences(
  idTurmaDisc,
  scheduleId,
  occurrenceDate,
  students
) {
  if (!Array.isArray(students)) {
    return students;
  }
  const sessionKey = demoOccurrenceSessionKey(
    occurrenceDate,
    idTurmaDisc,
    scheduleId
  );
  const snap = loadDemoOccurrenceSession(sessionKey);
  if (!snap || !snap.byRa) {
    return students;
  }

  return students.map(s => {
    const ra = String(s.ra || s.id);
    const p = snap.byRa[ra];
    if (!p) {
      return s;
    }
    return {
      ...s,
      occurrences: Array.isArray(p.occurrences) ? p.occurrences : s.occurrences,
      absence: typeof p.absence === "boolean" ? p.absence : s.absence
    };
  });
}

/** JSON completo (todas as sessões) — útil para exportar / inspecionar. */
export function exportDemoOccurrencesJson() {
  return JSON.stringify(readRoot(), null, 2);
}

/** Limpa persistência demo (ex.: reset manual). */
export function clearDemoOccurrencesPersistence() {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
